import React, { useEffect, useState } from "react";
import { useFilterBuilderContext } from "../FilterBuilderContext";
import useFilterDetails, {
  getFilterConditionByField,
} from "../../useFilterDetails";
import { TextButton, REQUIRED_ERROR_MESSAGE } from "@SeemplicityDev/shared";
import { Controller, FormProvider, useForm } from "react-hook-form";
import "./FilterDetailsForm.scss";
import FilterConditionSelect from "./FilterConditionSelect";
import FilterValueSelect from "./FilterValueSelect/FilterValueSelect";
import type { FilterSelectionValue } from "./FilterValueSelect/FilterValueSelection";
import type { FilterItem } from "../../../FilterManagement/CommonFilterTypes";

const controlTypeOptions = {
  field: "field",
  condition: "condition",
  value: "value",
} as const;

type ControlType = (typeof controlTypeOptions)[keyof typeof controlTypeOptions];

// In-flight builder form state: while the user is still choosing, `condition`
// and `value` can be null/undefined (defaultValues and the "Back" reset both
// write null). FilterItem disallows null on `value`, so widen it here; on
// submit the row is complete and `data` is cast back to FilterItem at the
// onFilterSelect boundary (runtime unchanged from the pre-migration JS).
type FilterDetailsFormValues = Omit<FilterItem, "value" | "condition"> & {
  condition?: string | null;
  value?: FilterItem["value"] | null;
};

const FilterDetailsForm: React.FC = () => {
  const [activeControl, setActiveControl] = useState<ControlType>(
    controlTypeOptions.value
  );
  const [isValueValid, setIsValueValid] = useState(false);

  const {
    filterBuilderState: {
      currentFilterField,
      fieldsData,
      currentFilter,
      onFilterSelect,
      onCancel,
      filtersToPin,
    },
    selectFilterField,
  } = useFilterBuilderContext();

  const methods = useForm<FilterDetailsFormValues>({
    mode: "all",
    defaultValues: {
      field: currentFilterField,
      condition:
        currentFilterField === currentFilter?.field
          ? currentFilter?.condition
          : null,
      value:
        currentFilterField === currentFilter?.field
          ? currentFilter?.value
          : null,
    },
  });
  const {
    handleSubmit,
    getValues,
    setValue,
    control,
    formState: { isValid, isDirty },
  } = methods;

  const { fieldData, conditionData, conditionOptions } = useFilterDetails(
    fieldsData,
    currentFilterField,
    currentFilter?.condition,
    currentFilter?.value,
    [],
    filtersToPin
  );

  const onSubmitFilter = (data: FilterDetailsFormValues) => {
    // On submit the row is fully populated (field/condition/value all set), so
    // the in-flight null/undefined widening collapses back to a real FilterItem.
    onFilterSelect(data as FilterItem);
  };

  useEffect(() => {
    setValue(
      "condition",
      conditionData?.value ||
        getFilterConditionByField(currentFilterField, fieldsData)
    );
  }, [currentFilterField, conditionData]);

  return (
    <div className={"filter-details-wrapper"}>
      {fieldData && (
        <>
          {!fieldData?.pinned && (
            <div className="filter-back-btn-wrapper">
              <TextButton
                data-testid={"back-to-filters"}
                label={"Back to all filters"}
                leftIcon={"seem-icon-left-arrow"}
                buttonStyle={"textLink"}
                onClick={() => {
                  setValue("value", null);
                  selectFilterField(null);
                }}
                type={"button"}
              />
            </div>
          )}
          <FormProvider {...methods}>
            <form
              className={"filter-details-form"}
              onSubmit={(e) => {
                e.stopPropagation();
                handleSubmit(onSubmitFilter)(e);
              }}
            >
              <div className={"filter-row"}>
                <Controller
                  name={`field`}
                  render={() => (
                    <div
                      className={"field-selected-value is-disabled"}
                      data-testid={`filter-field-${currentFilterField}-chip`}
                    >
                      {fieldData?.label}
                    </div>
                  )}
                  defaultValue={currentFilterField}
                  rules={{ required: REQUIRED_ERROR_MESSAGE }}
                  control={control}
                />
                <Controller
                  name={`condition`}
                  render={({ field: { onChange, value } }) => {
                    return (
                      <FilterConditionSelect
                        conditionOptions={conditionOptions}
                        // `condition` holds null while unset; the chip prop is
                        // string | undefined, so map the empty case to undefined
                        // (runtime-identical: a null condition matched no option).
                        currentValue={value ?? undefined}
                        onChange={(selectedValue, shouldResetValue) => {
                          onChange(selectedValue);
                          if (shouldResetValue) {
                            setValue(`value`, null);
                          }
                          setActiveControl(controlTypeOptions.value);
                        }}
                        onClick={() =>
                          setActiveControl(controlTypeOptions.condition)
                        }
                        isActive={
                          activeControl === controlTypeOptions.condition
                        }
                      />
                    );
                  }}
                  defaultValue={
                    currentFilter?.condition ||
                    // JS called this with a single arg; fieldsData was undefined
                    // at runtime, so pass undefined explicitly (2-arg signature).
                    getFilterConditionByField(currentFilterField, undefined)
                  }
                  rules={{ required: REQUIRED_ERROR_MESSAGE }}
                  control={control}
                />
                <Controller
                  name={`value`}
                  shouldUnregister={true}
                  render={({ field: { onChange, value } }) => {
                    return (
                      <FilterValueSelect
                        conditionOptions={conditionOptions}
                        // The form value union (FilterItem["value"] | null) and
                        // FilterSelectionValue describe the same runtime values;
                        // they differ only in object member shape, so bridge it.
                        currentValue={value as FilterSelectionValue}
                        onChange={(selectedValue) => {
                          onChange(selectedValue);
                        }}
                        onClick={() =>
                          setActiveControl(controlTypeOptions.value)
                        }
                        isActive={activeControl === controlTypeOptions.value}
                        setValid={(isValid) => setIsValueValid(isValid)}
                        filtersToPin={filtersToPin}
                      />
                    );
                  }}
                  rules={{
                    required: REQUIRED_ERROR_MESSAGE,
                    validate: () => {
                      const value = getValues(`value`);
                      if (typeof value === "object" && !Array.isArray(value)) {
                        return !!value?.value;
                      }
                      // Matches JS `value?.length > 0`; `!!value?.length` is the
                      // type-safe, runtime-identical form for string | string[].
                      return !!value?.length;
                    },
                  }}
                  control={control}
                />
              </div>
              <div className="filter-row-btn-wrapper form-button-group">
                <TextButton
                  label={"Cancel"}
                  type={"button"}
                  onClick={onCancel}
                />
                <TextButton
                  label={"Add"}
                  disabled={!isValid || !isDirty || !isValueValid}
                  type={"submit"}
                  buttonStyle={"primary"}
                  wrapperClassName={"filter-details-submit-button"}
                  data-testid={"filter-submit"}
                />
              </div>
            </form>
          </FormProvider>
        </>
      )}
    </div>
  );
};

export default FilterDetailsForm;
